import styled, { css, type Space } from 'styled-components';
import { Children, type ReactNode } from 'react';

import { Grid } from '@yoweb/ui/components/Grid';
import { getSpace } from '@yoweb/ui/styles/utils/theme';
import { media } from '@yoweb/ui/styles/utils';
import { useIntlMeta } from '@yoweb/ui/hooks/useIntlMeta';
import { hasReversedNames, hasPronunciationFields } from '@yoweb/utils/intl/names';

type NameGridProps = {
  /** children nodes, supposedly name fields, to display inside the grid */
  children: ReactNode[];

  /** whether the name grid is meant to display pronunciation fields */
  isPronunciation?: boolean;

  /** whether the given name fields will need to have pronunciation fields (ex. don't need pronunciation fields if the user will not be spoken to) */
  needsPronunciationFields?: boolean;

  /** the gap that will be used to separate the children */
  rowGap?: keyof Space;

  /** the gap (provided in proper CSS units like "rem" and not like "normal1") that will be used to separate the children */
  rowGapWidth?: string;

  /**
   * in some special cases, locales which should have the first and last names reversed actually
   * want the names printed in the normal order (ex. payment form names for JP)
   */
  forceNormalOrder?: boolean;

  'data-testid'?: string;
};

function NameGrid({
  children,
  isPronunciation,
  needsPronunciationFields,
  rowGap = 'normal1',
  rowGapWidth,
  'data-testid': dataTestId,
  forceNormalOrder,
}: NameGridProps) {
  const { lcLocale } = useIntlMeta();
  const newChildren = children;

  needsPronunciationFields =
    needsPronunciationFields === undefined
      ? hasPronunciationFields(lcLocale)
      : needsPronunciationFields;

  if (isPronunciation && !needsPronunciationFields) {
    return null;
  }

  if (hasReversedNames(lcLocale)) {
    return (
      <StyledNameGrid
        needsPronunciationFields={needsPronunciationFields}
        rowGap={rowGap}
        rowGapWidth={rowGapWidth}
        data-testid={dataTestId}
      >
        {forceNormalOrder ? newChildren : Children.toArray(newChildren).reverse()}
      </StyledNameGrid>
    );
  }

  return (
    <StyledNameGrid
      needsPronunciationFields={needsPronunciationFields}
      rowGap={rowGap}
      rowGapWidth={rowGapWidth}
      data-testid={dataTestId}
    >
      {newChildren}
    </StyledNameGrid>
  );
}

type StyledNameGridProps = {
  children: ReactNode[];
  needsPronunciationFields: boolean;
  rowGap: keyof Space;
  rowGapWidth?: string;
};

const StyledNameGrid = styled(Grid)<StyledNameGridProps>`
  && {
    row-gap: ${({ rowGapWidth, rowGap }) => rowGapWidth ?? getSpace(rowGap)};

    ${({ needsPronunciationFields }) =>
      needsPronunciationFields &&
      css`
        grid-template-columns: 1fr;

        ${media.md`
          grid-template-columns: repeat(2, 1fr);
        `}
      `}
  }
`;

export default NameGrid;
